/**
 * PDF generator for Lost Luggage claim summaries.
 *
 * Two modes:
 *   withPhotos = true  → text + embedded JPEG luggage photos (default, best for
 *                        airline claim submissions where visual ID helps).
 *   withPhotos = false → text-only PDF, smaller file, faster upload, easier to
 *                        print or paste into portals with size limits.
 *
 * Layout: A4 portrait, 14pt heading, 10pt body, 1 cm left/right margin.
 */
import jsPDF from "jspdf";

const MARGIN = 36; // ~12.7mm at 72dpi
const LINE = 14;
const PAGE_W = 595; // A4 width  @ 72dpi
const PAGE_H = 842; // A4 height @ 72dpi
const BODY_W = PAGE_W - MARGIN * 2;

function ensureSpace(doc, y, needed = LINE) {
  if (y + needed > PAGE_H - MARGIN) {
    doc.addPage();
    return MARGIN;
  }
  return y;
}

function drawHeader(doc) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(14, 44, 84); // navy
  doc.text("Lost / Delayed Baggage Claim", MARGIN, MARGIN + 4);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(74, 93, 120); // navy-soft
  doc.text(
    `Prepared with TipLingo · ${new Date().toLocaleString()}`,
    MARGIN,
    MARGIN + 20
  );
  // Divider
  doc.setDrawColor(229, 91, 44); // orange
  doc.setLineWidth(1.5);
  doc.line(MARGIN, MARGIN + 28, PAGE_W - MARGIN, MARGIN + 28);
  return MARGIN + 44;
}

function drawSectionTitle(doc, y, title) {
  y = ensureSpace(doc, y, 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(14, 44, 84);
  doc.text(title.toUpperCase(), MARGIN, y);
  doc.setDrawColor(229, 91, 44);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y + 3, MARGIN + 50, y + 3);
  return y + 16;
}

function drawKV(doc, y, label, value) {
  if (!value && value !== 0) return y;
  y = ensureSpace(doc, y, LINE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(14, 44, 84);
  doc.text(`${label}:`, MARGIN, y);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(36, 49, 64);
  const labelW = doc.getTextWidth(`${label}: `);
  const lines = doc.splitTextToSize(String(value), BODY_W - labelW - 6);
  doc.text(lines, MARGIN + labelW + 4, y);
  return y + LINE * lines.length;
}

function drawParagraph(doc, y, text) {
  if (!text) return y;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(36, 49, 64);
  const lines = doc.splitTextToSize(String(text), BODY_W);
  lines.forEach((ln) => {
    y = ensureSpace(doc, y, LINE);
    doc.text(ln, MARGIN, y);
    y += LINE;
  });
  return y;
}

async function drawPhotoGrid(doc, y, photos) {
  if (!photos?.length) return y;
  y = drawSectionTitle(doc, y, "Luggage Photos");
  const cellW = (BODY_W - 12) / 2; // 2 columns
  const cellH = cellW * 0.75;
  let col = 0;
  for (const p of photos) {
    if (!p?.data_url) continue;
    if (col === 0) y = ensureSpace(doc, y, cellH + 8);
    const x = MARGIN + col * (cellW + 12);
    try {
      doc.addImage(p.data_url, "JPEG", x, y, cellW, cellH, undefined, "MEDIUM");
    } catch {
      // Fall back to a placeholder rectangle if the data URL can't be decoded.
      doc.setDrawColor(74, 93, 120);
      doc.rect(x, y, cellW, cellH);
      doc.setFontSize(9);
      doc.setTextColor(74, 93, 120);
      doc.text("Photo unavailable", x + 8, y + cellH / 2);
    }
    col += 1;
    if (col === 2) {
      col = 0;
      y += cellH + 12;
    }
  }
  if (col === 1) y += cellH + 12;
  return y;
}

function drawDisclaimer(doc) {
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(74, 93, 120);
  const text =
    "TipLingo helps prepare your claim. Final submission must be completed " +
    "through the airline's official baggage claim portal. TipLingo does not " +
    "guarantee claim approval, baggage recovery, or airline response time.";
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    const lines = doc.splitTextToSize(text, BODY_W);
    doc.text(lines, MARGIN, PAGE_H - 24);
    doc.text(`Page ${i} of ${pages}`, PAGE_W - MARGIN, PAGE_H - 12, {
      align: "right",
    });
  }
}

export async function generateClaimPdf(claim, { withPhotos = true } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  let y = drawHeader(doc);

  // Traveler
  y = drawSectionTitle(doc, y, "Traveler Information");
  y = drawKV(doc, y, "Name", claim.traveler_name);
  y = drawKV(doc, y, "Phone", claim.traveler_phone);
  y = drawKV(doc, y, "Email", claim.traveler_email);
  y = drawKV(doc, y, "Address", claim.traveler_address);
  y = drawKV(doc, y, "Preferred Language", claim.preferred_language);
  y += 6;

  // Flight
  y = drawSectionTitle(doc, y, "Flight Information");
  y = drawKV(doc, y, "Airline", claim.airline_name);
  y = drawKV(doc, y, "Flight Number", claim.flight_number);
  y = drawKV(doc, y, "Date of Travel", claim.travel_date);
  y = drawKV(doc, y, "Departure Airport", claim.departure_airport);
  y = drawKV(doc, y, "Arrival Airport", claim.arrival_airport);
  y = drawKV(doc, y, "Connecting Airport", claim.connecting_airport);
  y = drawKV(doc, y, "Booking Reference", claim.booking_reference);
  y = drawKV(doc, y, "Ticket Number", claim.ticket_number);
  y = drawKV(doc, y, "Baggage Tag Number", claim.baggage_tag_number);
  y = drawKV(doc, y, "Checked Bags", claim.checked_bag_count);
  y += 6;

  // Bag
  y = drawSectionTitle(doc, y, "Luggage Description");
  y = drawKV(doc, y, "Bag Type", claim.bag_type);
  y = drawKV(doc, y, "Brand", claim.brand);
  y = drawKV(doc, y, "Color", claim.color);
  y = drawKV(doc, y, "Size", claim.size);
  y = drawKV(doc, y, "Material", claim.material);
  y = drawKV(doc, y, "Identifying Marks", claim.identifying_marks);
  y = drawKV(doc, y, "Estimated Value", claim.estimated_value);
  if (claim.contents_summary) {
    y += 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(14, 44, 84);
    y = ensureSpace(doc, y, LINE);
    doc.text("Contents Summary:", MARGIN, y);
    y += LINE;
    y = drawParagraph(doc, y, claim.contents_summary);
  }
  y += 6;

  // Incident
  y = drawSectionTitle(doc, y, "Incident Details");
  y = drawKV(doc, y, "Airport Where Reported Missing", claim.incident_airport);
  y = drawKV(doc, y, "Time Discovered Missing", claim.time_discovered_missing);
  if (claim.additional_notes) {
    y += 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(14, 44, 84);
    y = ensureSpace(doc, y, LINE);
    doc.text("Additional Notes:", MARGIN, y);
    y += LINE;
    y = drawParagraph(doc, y, claim.additional_notes);
  }
  y += 6;

  // Reference & Status
  if (claim.airline_claim_reference || claim.status) {
    y = drawSectionTitle(doc, y, "Claim Reference");
    y = drawKV(doc, y, "Airline Claim Reference", claim.airline_claim_reference);
    y = drawKV(doc, y, "Status", claim.status);
  }

  if (withPhotos && claim.photos?.length) {
    y += 8;
    y = await drawPhotoGrid(doc, y, claim.photos);
  }

  drawDisclaimer(doc);

  const safeAirline = (claim.airline_name || "airline").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
  const safeFlight = (claim.flight_number || "flight").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
  const tag = withPhotos ? "with_photos" : "text_only";
  doc.save(`tiplingo_baggage_claim_${safeAirline}_${safeFlight}_${tag}.pdf`);
}
